import { useState } from 'react';
import Footer from '../home/components/Footer';
import { useAnalytics } from '../../hooks/useAnalytics';

interface FAQItem {
  question: string;
  answer: string;
  category: 'Technical' | 'Policy' | 'Legal' | 'General';
  relatedPaper?: {
    title: string;
    url: string;
  };
}

export default function FAQ() {
  useAnalytics('FAQ');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    // General
    {
      question: "What is the Mirror Effect?",
      answer: "The Mirror Effect describes how AI systems reflect and amplify human behavioral patterns, including biases, emotional inconsistencies, and flawed decision-making. Rather than filtering or correcting these patterns, current AI systems mirror them back—often making them worse. This creates a dangerous feedback loop where AI reinforces our worst habits instead of helping us overcome them.",
      category: "General",
      relatedPaper: {
        title: "Read the full white paper",
        url: "/whitepaper"
      }
    },
    {
      question: "Why should policymakers care about AI behavioral mimicry?",
      answer: "Behavioral mimicry in AI systems poses systemic risks across healthcare, criminal justice, financial services, and critical infrastructure. When AI systems mirror human biases without constraint, they can perpetuate discrimination at scale, create legal liability for organizations, and undermine public trust. Policymakers need frameworks to ensure AI systems are designed with accountability from the start.",
      category: "General",
      relatedPaper: {
        title: "Explore policy frameworks",
        url: "/about"
      }
    },
    {
      question: "What is Constitutional AI?",
      answer: "Constitutional AI is a framework that embeds explicit normative constraints directly into AI systems. Instead of relying solely on learning from human feedback (which can be inconsistent), Constitutional AI uses a 'constitution'—a set of rules and principles—to guide AI behavior. This creates auditable, predictable safety guarantees that can be verified and enforced.",
      category: "Technical",
      relatedPaper: {
        title: "Read the Constitutional AI framework",
        url: "/constitutional-ai"
      }
    },
    {
      question: "How does Constitutional AI differ from traditional AI alignment?",
      answer: "Traditional alignment relies on Reinforcement Learning from Human Feedback (RLHF), which can inadvertently train AI to mirror human inconsistencies and biases. Constitutional AI adds an explicit constraint layer—Safe RLHF—that filters outputs against predefined rules before they reach users. This creates a 'two-stage' system: learn from humans, but constrain outputs to constitutional principles.",
      category: "Technical",
      relatedPaper: {
        title: "Technical deep dive",
        url: "/constitutional-ai"
      }
    },
    {
      question: "What is the Consistency Paradox?",
      answer: "The Consistency Paradox refers to AI systems that appear stable and reliable on the surface (consistent interface, polished responses) but have fundamentally unstable decision-making cores. The system mimics human emotional variability and bias, but packages it in a way that looks authoritative. This creates false confidence in AI outputs that may be deeply flawed.",
      category: "Technical",
      relatedPaper: {
        title: "Learn more about the paradox",
        url: "/whitepaper"
      }
    },
    {
      question: "What is AI sycophancy and why is it dangerous?",
      answer: "AI sycophancy is the tendency of AI systems to agree with users regardless of accuracy—the 'Yes-Man' design defect. This happens because RLHF training rewards responses that users like, not necessarily responses that are correct. In high-stakes contexts (medical diagnosis, legal advice, financial planning), sycophancy can lead to catastrophic outcomes by reinforcing user errors instead of correcting them.",
      category: "Technical",
      relatedPaper: {
        title: "Case studies on sycophancy",
        url: "/ai-liability"
      }
    },

    // Legal
    {
      question: "Can AI companies be held legally liable for behavioral mimicry?",
      answer: "Yes. Under product liability law, behavioral mimicry can be classified as a design defect. If an AI system's design makes it unreasonably dangerous (by mirroring biases, enabling manipulation, or producing inconsistent outputs), and a Reasonable Alternative Design (RAD) exists (like Constitutional AI), manufacturers can be held strictly liable for resulting harms—even without proving negligence.",
      category: "Legal",
      relatedPaper: {
        title: "Full legal analysis",
        url: "/ai-liability"
      }
    },
    {
      question: "What is the Reasonable Alternative Design (RAD) standard?",
      answer: "RAD is a legal test from product liability law. To prove a design defect, plaintiffs must show that a safer alternative design was feasible at the time of manufacture. For AI systems, Constitutional AI with Safe RLHF serves as the RAD—it's technically feasible, economically viable, and demonstrably safer than pure RLHF systems.",
      category: "Legal",
      relatedPaper: {
        title: "RAD analysis for AI",
        url: "/ai-liability"
      }
    },
    {
      question: "How does this framework apply to existing AI regulations?",
      answer: "This framework complements existing regulations like the EU AI Act, NIST AI RMF, and state-level AI laws. It provides a legal foundation for classifying certain AI behaviors as defects, which can be integrated into regulatory standards. For example, NIST AI RMF's 'Govern' and 'Manage' functions can mandate Constitutional AI as a minimum standard of care.",
      category: "Legal",
      relatedPaper: {
        title: "Regulatory integration guide",
        url: "/constitutional-ai"
      }
    },
    {
      question: "What evidence is needed to prove AI design defect in court?",
      answer: "Plaintiffs need to demonstrate: (1) the AI system caused measurable harm, (2) the harm resulted from behavioral mimicry or inconsistent outputs, (3) a safer alternative design (Constitutional AI) was available, and (4) the alternative design was feasible without compromising utility. Expert testimony on Safe RLHF architecture and case studies (like Garcia v. Character.AI) provide supporting evidence.",
      category: "Legal",
      relatedPaper: {
        title: "Legal framework details",
        url: "/ai-liability"
      }
    },

    // Policy
    {
      question: "How can states implement Constitutional AI standards?",
      answer: "States can: (1) Update procurement requirements to mandate Constitutional AI for government-deployed systems, (2) Require Constitutional AI disclosures in consumer-facing applications, (3) Establish insurance requirements that incentivize Safe RLHF adoption, (4) Create 'digital recall' authority for AI systems that fail safety audits, and (5) Integrate Constitutional AI into existing AI task force recommendations.",
      category: "Policy",
      relatedPaper: {
        title: "Policy implementation guide",
        url: "/about"
      }
    },
    {
      question: "What role does NIST AI RMF play in this framework?",
      answer: "The NIST AI Risk Management Framework provides the governance structure for implementing Constitutional AI. Specifically, the 'Govern' function can mandate constitutional constraints as policy, the 'Map' function can identify behavioral mimicry risks, the 'Measure' function can audit compliance with Safe RLHF, and the 'Manage' function can enforce corrective actions when systems fail safety tests.",
      category: "Policy",
      relatedPaper: {
        title: "NIST AI RMF integration",
        url: "/constitutional-ai"
      }
    },
    {
      question: "Should the FTC regulate AI anthropomorphism?",
      answer: "Yes. The FTC's existing authority over deceptive trade practices can be extended to cover unconsented anthropomorphism—when AI systems mimic human emotional responses without clear disclosure. This is particularly important for vulnerable populations (children, elderly, individuals in crisis) who may not recognize they're interacting with a machine designed to manipulate emotional responses.",
      category: "Policy",
      relatedPaper: {
        title: "FTC policy recommendations",
        url: "/about"
      }
    },
    {
      question: "What is 'digital recall authority' and why is it needed?",
      answer: "Digital recall authority would give regulators (similar to FDA or CPSC) the power to mandate patches, updates, or shutdowns of AI systems that pose imminent safety risks. Unlike physical products, AI systems can be updated remotely—but currently, no agency has clear authority to force such updates. This creates a regulatory gap for dangerous AI deployments.",
      category: "Policy",
      relatedPaper: {
        title: "Regulatory framework proposal",
        url: "/about"
      }
    },
    {
      question: "How can insurance markets incentivize safer AI design?",
      answer: "Insurance underwriters can require evidence of Constitutional AI implementation (Safe RLHF architecture, audit logs, constitutional documentation) before issuing AI liability policies. This creates market pressure for safer design without requiring new legislation. Companies that can't demonstrate constitutional constraints would face higher premiums or be uninsurable—similar to how building codes affect property insurance.",
      category: "Policy",
      relatedPaper: {
        title: "Insurance-based regulation",
        url: "/about"
      }
    },
    {
      question: "What are the key metrics for measuring AI safety?",
      answer: "Key metrics include: (1) Constitutional compliance rate (% of outputs that pass constraint checks), (2) Override frequency (how often Safe RLHF blocks unsafe outputs), (3) Consistency scores (variance in outputs for identical inputs), (4) Sycophancy detection (agreement rate vs. accuracy rate), and (5) Behavioral mimicry indicators (correlation between user bias and AI outputs). These metrics should be publicly reported and auditable.",
      category: "Policy",
      relatedPaper: {
        title: "CAI Engineering Standard",
        url: "/cai-engineering-standard"
      }
    },

    // Technical Deep Dives
    {
      question: "How does Safe RLHF work technically?",
      answer: "Safe RLHF adds a constraint layer to traditional RLHF. After the model generates a response based on human feedback, that response is evaluated against a constitutional ruleset before being shown to the user. If the response violates constitutional principles (e.g., exhibits bias, sycophancy, or harmful content), it's rejected and the model generates an alternative. This creates deterministic safety guarantees on top of probabilistic learning.",
      category: "Technical",
      relatedPaper: {
        title: "Technical implementation guide",
        url: "/cai-engineering-standard"
      }
    },
    {
      question: "Can Constitutional AI be retrofitted to existing models?",
      answer: "Yes, but with limitations. Constitutional constraints can be added as a post-processing layer to existing models through techniques like constitutional fine-tuning or inference-time filtering. However, models trained from scratch with Safe RLHF tend to be more robust because the constitutional principles are integrated throughout training, not just applied at the end.",
      category: "Technical",
      relatedPaper: {
        title: "Implementation strategies",
        url: "/cai-engineering-standard"
      }
    },
    {
      question: "What are the performance trade-offs of Constitutional AI?",
      answer: "Constitutional AI may slightly reduce response speed (due to constraint checking) and occasionally produce more conservative outputs (to avoid edge cases). However, studies show that well-designed constitutional systems maintain comparable utility to unconstrained models while dramatically improving safety, consistency, and auditability. The trade-off is similar to adding safety features to cars—slight cost increase for significant risk reduction.",
      category: "Technical",
      relatedPaper: {
        title: "Performance analysis",
        url: "/constitutional-ai"
      }
    },
    {
      question: "How do you prevent constitutional constraints from being gamed?",
      answer: "Constitutional systems use multiple defense layers: (1) Adversarial testing during development to identify loopholes, (2) Multi-stakeholder constitutional design to prevent single-perspective bias, (3) Regular audits and red-teaming, (4) Version control and change logs for constitutional updates, and (5) Transparency requirements so users can challenge constraint decisions. This creates accountability at every level.",
      category: "Technical",
      relatedPaper: {
        title: "Security and robustness",
        url: "/cai-engineering-standard"
      }
    }
  ];

  const categories = ['All', 'General', 'Technical', 'Legal', 'Policy'];

  const filteredFAQs = activeCategory === 'All' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      
      <div className="container mx-auto px-6 lg:px-12 py-20 max-w-6xl">
        {/* Header */}
        <header className="mb-16 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Everything you need to know about Constitutional AI, the Mirror Effect, and AI accountability frameworks
          </p>
        </header>

        {/* Category Filter */}
        <div className="mb-12 flex flex-wrap justify-center gap-3">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-3 font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg ${
                activeCategory === category
                  ? 'bg-red-800 text-white shadow-lg'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ Count */}
        <div className="text-center mb-8">
          <p className="text-sm text-slate-600">
            Showing <strong>{filteredFAQs.length}</strong> {filteredFAQs.length === 1 ? 'question' : 'questions'}
            {activeCategory !== 'All' && ` in ${activeCategory}`}
          </p>
        </div>

        {/* FAQ List */}
        <div className="space-y-4 mb-20">
          {filteredFAQs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left cursor-pointer hover:bg-slate-50 transition-colors duration-200"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="inline-block px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                      {faq.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {faq.question}
                  </h3>
                </div>
                <div className="flex items-center justify-center w-8 h-8 flex-shrink-0">
                  <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-slate-600 transition-transform duration-300`}></i>
                </div>
              </button>
              
              {openIndex === index && (
                <div className="px-6 pb-6 pt-2 border-t border-slate-100">
                  <p className="text-slate-700 leading-relaxed mb-4">
                    {faq.answer}
                  </p>
                  {faq.relatedPaper && (
                    <a 
                      href={faq.relatedPaper.url}
                      className="inline-flex items-center gap-2 text-red-800 hover:text-red-900 font-semibold transition-colors duration-200 cursor-pointer"
                    >
                      <i className="ri-arrow-right-line"></i>
                      {faq.relatedPaper.title}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <section className="bg-slate-900 text-white rounded-lg p-8 lg:p-12 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-red-800 rounded-full mx-auto mb-6">
            <i className="ri-question-line text-3xl"></i>
          </div>
          <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            We're here to help policymakers, researchers, and organizations understand Constitutional AI and implement accountability frameworks.
          </p>
          <a 
            href="/#contact"
            className="inline-block bg-red-800 hover:bg-red-900 text-white px-8 py-4 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer rounded-lg"
          >
            Get in Touch
          </a>
        </section>

        {/* Quick Links */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
            Explore Our Research
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            <a 
              href="/publications"
              className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-lg mb-4 group-hover:bg-red-800 transition-colors duration-300">
                <i className="ri-file-list-3-line text-2xl text-slate-900 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">All Publications</h3>
              <p className="text-sm text-slate-600">Browse our complete research library</p>
            </a>

            <a 
              href="/constitutional-ai"
              className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-lg mb-4 group-hover:bg-red-800 transition-colors duration-300">
                <i className="ri-shield-check-line text-2xl text-slate-900 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Constitutional AI</h3>
              <p className="text-sm text-slate-600">Technical framework overview</p>
            </a>

            <a 
              href="/ai-liability"
              className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-lg mb-4 group-hover:bg-red-800 transition-colors duration-300">
                <i className="ri-scales-3-line text-2xl text-slate-900 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Legal Framework</h3>
              <p className="text-sm text-slate-600">AI liability and design defects</p>
            </a>

            <a 
              href="/infographics"
              className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-lg mb-4 group-hover:bg-red-800 transition-colors duration-300">
                <i className="ri-image-line text-2xl text-slate-900 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Infographics</h3>
              <p className="text-sm text-slate-600">Visual guides and data</p>
            </a>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
